import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Ticket, X } from "lucide-react";
import { Link } from "react-router-dom";

const PLAN_BADGE = {
    "plan-explorer": "bg-navy-50 text-navy-700 border-navy-100 border",
    "plan-boost": "bg-gold-500 text-navy-900",
    "plan-prime": "bg-navy-700 text-white",
};

const NONE = "__none__";

function formatDiscount(p) {
    if (!p) return null;
    return p.discount_type === "percent"
        ? `${p.discount_value}% off`
        : `HK$${p.discount_value.toFixed(0)} off`;
}

export default function Memberships() {
    const [rows, setRows] = useState([]);
    const [promos, setPromos] = useState([]);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        const [m, p] = await Promise.all([
            api.get("/admin/memberships"),
            api.get("/admin/promo-codes"),
        ]);
        setRows(m.data);
        setPromos(p.data);
    };

    useEffect(() => {
        load();
    }, []);

    const counts = useMemo(
        () =>
            rows.reduce((acc, r) => {
                acc[r.plan.id] = (acc[r.plan.id] || 0) + 1;
                return acc;
            }, {}),
        [rows]
    );

    const assignablePromos = useMemo(
        () =>
            promos.filter(
                (p) =>
                    p.active &&
                    (p.applies_to === "all" || p.applies_to === "membership")
            ),
        [promos]
    );

    const attach = async (userId, promoId) => {
        setBusyId(userId);
        try {
            await api.put(`/admin/memberships/${userId}/promo`, {
                promo_code_id: promoId === NONE ? null : promoId,
            });
            toast.success(
                promoId === NONE ? "Promo detached" : "Promo attached to member"
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Membership"
                title="Member subscriptions"
                description="Distribution of members across the three Perx tiers. Attach a promo code to any member — codes come from your Promo codes library."
                testid="admin-memberships-header"
                action={
                    <Link to="/admin/promo-codes">
                        <Button
                            variant="outline"
                            data-testid="memberships-manage-promos"
                            className="rounded-full border-navy-700 text-navy-700 h-11 px-5"
                        >
                            <Ticket size={16} className="mr-2" /> Manage promo codes
                        </Button>
                    </Link>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <div
                        className="rounded-2xl bg-white shadow-soft p-6"
                        data-testid="plan-count-plan-boost"
                    >
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                            Active members
                        </div>
                        <div className="font-display text-4xl text-navy-700">
                            {counts["plan-boost"] || 0}
                        </div>
                        <div className="text-navy-500 text-sm mt-1">
                            active subscriptions
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                            Total members
                        </div>
                        <div className="font-display text-4xl text-navy-700">
                            {rows.length}
                        </div>
                        <div className="text-navy-500 text-sm mt-1">
                            across the platform
                        </div>
                    </div>
                    <div className="rounded-2xl bg-navy-700 text-white shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-2">
                            With active promo
                        </div>
                        <div className="font-display text-4xl">
                            {rows.filter((r) => r.membership_promo).length}
                        </div>
                        <div className="text-navy-50/60 text-sm mt-1">
                            attached to their plan
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="memberships-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Member
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Email
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Plan
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Joined
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em] min-w-[240px]">
                                    Attached promo
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((u) => {
                                const attached = u.membership_promo;
                                const selectValue = attached ? attached.id : NONE;
                                return (
                                    <TableRow key={u.id} data-testid={`ms-row-${u.id}`}>
                                        <TableCell className="text-navy-700 font-medium">
                                            {u.name}
                                            {u.signup_promo && (
                                                <div className="text-[10px] uppercase tracking-widest text-navy-400 mt-1">
                                                    Signed up with {u.signup_promo.code}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            {u.email}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                className={`${PLAN_BADGE[u.plan.id]} border-0 uppercase tracking-widest text-[10px]`}
                                            >
                                                {u.plan.name}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            {u.created_at
                                                ? new Date(u.created_at).toLocaleDateString()
                                                : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Select
                                                    value={selectValue}
                                                    onValueChange={(v) => attach(u.id, v)}
                                                    disabled={busyId === u.id}
                                                >
                                                    <SelectTrigger
                                                        data-testid={`ms-promo-select-${u.id}`}
                                                        className="h-9 text-sm w-[200px]"
                                                    >
                                                        <SelectValue placeholder="No promo attached" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value={NONE}>
                                                            — No promo —
                                                        </SelectItem>
                                                        {assignablePromos.map((p) => (
                                                            <SelectItem key={p.id} value={p.id}>
                                                                {p.code} · {formatDiscount(p)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {attached && (
                                                    <button
                                                        type="button"
                                                        title="Detach promo"
                                                        data-testid={`ms-promo-detach-${u.id}`}
                                                        onClick={() => attach(u.id, NONE)}
                                                        disabled={busyId === u.id}
                                                        className="text-navy-500 hover:text-red-600 p-1"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            {attached && (
                                                <div className="text-[11px] text-emerald-700 mt-1">
                                                    {formatDiscount(attached)}
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}
